import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/v2/components/ui/dialog";
import { Button } from "@/v2/components/ui/button";
import { Badge } from "@/v2/components/ui/badge";
import {
  tabRowStyle,
  tabButtonStyle,
  activeUnderlineStyle,
} from "@/v2/components/underline-tabs";

function statusVariant(status: string): "default" | "destructive" | "secondary" {
  if (status === "accepted") return "default";
  if (status === "declined") return "destructive";
  return "secondary";
}

export function ChannelSharesDialog({
  open,
  onClose,
  incomingRequests,
  shareActionLoading,
  onAccept,
  onDecline,
  onRefreshIncoming,
  onRefreshShares,
  channelShares,
  cancellingShareId,
  onCancelShare,
}: {
  open: boolean;
  onClose: () => void;
  incomingRequests: any[];
  shareActionLoading: string | null;
  onAccept: (token: string) => void;
  onDecline: (token: string) => void;
  onRefreshIncoming: () => void;
  onRefreshShares: () => void;
  channelShares: any[];
  cancellingShareId: number | null;
  onCancelShare: (shareId: number) => void;
}) {
  const [tab, setTab] = useState("requests");

  useEffect(() => {
    if (open) {
      onRefreshIncoming();
      onRefreshShares();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Spotlight Sharing</DialogTitle>
        </DialogHeader>

        {/* Plain buttons on a rule, not the shadcn Tabs pill group: the pill
            chrome (rounded fill, ring on the active trigger) drew on top of
            the underline. Same shape the Audience Layer's panels and the
            Studio use, from the shared style module. */}
        <div style={tabRowStyle} role="tablist">
          {/* Uppercased with text-transform rather than in the string, so the
              accessible name stays "Requests" / "Shared With". */}
          <button
            type="button"
            role="tab"
            aria-selected={tab === "requests"}
            onClick={() => setTab("requests")}
            style={{ ...tabButtonStyle(tab === "requests"), textTransform: "uppercase" }}
          >
            {incomingRequests.length > 0
              ? `Requests (${incomingRequests.length})`
              : "Requests"}
            {tab === "requests" && <span aria-hidden="true" style={activeUnderlineStyle} />}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "shared"}
            onClick={() => setTab("shared")}
            style={{ ...tabButtonStyle(tab === "shared"), textTransform: "uppercase" }}
          >
            Shared With
            {tab === "shared" && <span aria-hidden="true" style={activeUnderlineStyle} />}
          </button>
        </div>

        <div style={{ paddingTop: "16px" }}>
          {tab === "requests" ? (
            <>
            {incomingRequests.length === 0 ? (
              <p className="text-sm text-slate-500">No pending share requests</p>
            ) : (
              <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
                {incomingRequests.map((share: any) => (
                  <div
                    key={share.ID}
                    className="rounded-lg border border-slate-200"
                    style={{ padding: "10px 12px" }}
                  >
                    <p className="text-sm mb-2">
                      <strong>{share.OwnerName || share.OwnerEmail}</strong>{" "}
                      wants to share{" "}
                      {share.ChannelName ? (
                        <strong>"{share.ChannelName}"</strong>
                      ) : (
                        "a Spotlight"
                      )}{" "}
                      with you
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={!!shareActionLoading}
                        onClick={() => onAccept(share.Token)}
                        className="gap-1.5"
                      >
                        {shareActionLoading === `accept-${share.Token}` && (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        )}
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!!shareActionLoading}
                        onClick={() => onDecline(share.Token)}
                        className="gap-1.5"
                      >
                        {shareActionLoading === `decline-${share.Token}` && (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        )}
                        Decline
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
</>
          ) : (
            <>
            {channelShares.length === 0 ? (
              <p className="text-sm text-slate-500">
                You have not shared any Spotlights yet.
              </p>
            ) : (
              /* A four-column table could not hold a full email address inside
                 the dialog: the Recipient cell refused to wrap, so the rows
                 (and the Action heading) ran past the dialog's right edge.
                 Stacking each share into a card lets the address wrap, drops
                 the Action column that was empty for every non-pending row,
                 and matches the Requests tab beside it. */
              <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
                {channelShares.map((share: any) => (
                  <div
                    key={share.ID}
                    className="flex items-start justify-between gap-3 rounded-lg border border-slate-200"
                    style={{ padding: "10px 12px" }}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">
                        {share.ChannelName || `#${share.ChannelID}`}
                      </div>
                      <div className="text-xs text-slate-500 break-all">
                        {share.RecipientEmail}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge variant={statusVariant(share.Status)}>{share.Status}</Badge>
                      {share.Status === "pending" && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Cancel share"
                          disabled={cancellingShareId === share.ID}
                          onClick={() => onCancelShare(share.ID)}
                        >
                          {cancellingShareId === share.ID ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
</>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
